import { useState } from "react";
import { formatTL, resolveImage, effectivePrice, hasDiscount } from "@/lib/api";
import { Plus, Check, Tag } from "lucide-react";
import { useT } from "@/lib/kioskI18n";

export const ProductCard = ({ product, onAdd, index = 0 }) => {
  const [justAdded, setJustAdded] = useState(false);
  const { t, m } = useT();

  const handleAdd = () => {
    onAdd(product);
    if (!(product.removables && product.removables.length)) {
      setJustAdded(true);
      setTimeout(() => setJustAdded(false), 1100);
    }
  };

  return (
    <div
      data-testid={`kiosk-product-${product.id}`}
      onClick={handleAdd}
      className={`group relative bg-white rounded-2xl border overflow-hidden flex flex-col animate-fade-up transition-shadow cursor-pointer select-none active:scale-[0.98] ${
        justAdded
          ? "border-green-500 animate-added-flash"
          : "border-[var(--fb-border)] hover:shadow-[0_10px_30px_-5px_rgba(220,38,38,0.15)]"
      }`}
      style={{ animationDelay: `${index * 40}ms`, touchAction: "manipulation" }}
    >
      <div
        data-testid={`product-image-${product.id}`}
        className="relative h-24 sm:h-32 overflow-hidden bg-[var(--fb-neutral)]"
      >
        {product.image_url ? (
          <img
            src={resolveImage(product.image_url)}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-[var(--fb-muted)] text-sm">
            {t("Görsel yok")}
          </div>
        )}
        {hasDiscount(product) && (
          <span
            data-testid={`discount-badge-${product.id}`}
            className="absolute top-2 right-2 flex items-center gap-1 bg-[var(--fb-red)] text-white text-[10px] sm:text-xs font-extrabold px-2 py-1 rounded-full shadow-lg animate-pop"
          >
            <Tag className="w-3.5 h-3.5" />
            {product.discount_type === "amount"
              ? `-${formatTL(product.discount_value)}`
              : `%${product.discount_value} ${t("İNDİRİM")}`}
          </span>
        )}
        {justAdded && (
          <div
            data-testid={`added-overlay-${product.id}`}
            className="absolute inset-0 bg-green-600/90 grid place-items-center animate-added-overlay"
          >
            <div className="flex flex-col items-center gap-1.5 text-white">
              <div className="w-12 h-12 rounded-full bg-white grid place-items-center animate-pop">
                <Check className="w-7 h-7 text-green-600" strokeWidth={3} />
              </div>
              <span className="font-display font-bold text-sm">{t("Sepete Eklendi")}</span>
            </div>
          </div>
        )}
      </div>

      <div className="p-2.5 sm:p-3 flex flex-col flex-1">
        <h3 className="font-display font-bold text-sm sm:text-base leading-tight line-clamp-2">{m(product.name)}</h3>
        {hasDiscount(product) && product.campaign_name && (
          <span
            data-testid={`campaign-name-${product.id}`}
            className="inline-flex w-fit items-center gap-1 mt-1 bg-red-50 text-[var(--fb-red)] text-xs font-bold px-2 py-0.5 rounded-full"
          >
            <Tag className="w-3 h-3" /> {m(product.campaign_name)}
          </span>
        )}
        <p className="text-xs sm:text-sm text-[var(--fb-muted)] mt-1 line-clamp-2 flex-1">
          {m(product.description)}
        </p>
        <div className="flex flex-col gap-2 mt-3">
          {hasDiscount(product) ? (
            <span className="flex flex-col leading-none">
              <span className="text-xs text-[var(--fb-muted)] line-through">{formatTL(product.price)}</span>
              <span data-testid={`product-price-${product.id}`} className="font-display font-extrabold text-base sm:text-xl text-[var(--fb-red)]">
                {formatTL(effectivePrice(product))}
              </span>
            </span>
          ) : (
            <span data-testid={`product-price-${product.id}`} className="font-display font-extrabold text-base sm:text-xl text-[var(--fb-red)]">
              {formatTL(product.price)}
            </span>
          )}
          <button
            data-testid={`add-to-cart-${product.id}`}
            type="button"
            style={{ touchAction: "manipulation" }}
            className={`w-full justify-center flex items-center gap-1 font-bold text-sm sm:text-base px-3 py-2.5 sm:py-3 rounded-full transition-colors active:scale-95 ${
              justAdded
                ? "bg-green-600 text-white"
                : "bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white"
            }`}
          >
            {justAdded ? (
              <>
                <Check className="w-4 h-4" /> {t("Eklendi")}
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" /> {t("Ekle")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
